import * as CL from '@design-system/component-library';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { paths } from '../../common/constants/pathVariables.js';
import { DeviceListActionCoCo } from '../../common/enums.js';
import { createEmployeeProductList, createEmployeeProductSelection } from '../../common/fetch.js';
import { cancelMsg, somethingWentWrongMsg, t } from '../../common/i18n/index.js';
import {
  toCopyEmployeeProductListRequest,
  toCopyEmployeeProductSelectionRequest,
} from '../../common/utils/deviceListFormUtilsCoCo.js';
import { getDeviceListsTabPath } from '../../common/utils/deviceListTabsUtilsCoCo.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import { addNotification } from '../../public/site/notificationStore.js';
import { DialogWrapper } from '../DialogWrapper/index.js';
import { getUserAccounts } from '../Header/dynamic/headerFunctions.js';
import type { EmployeeProductListResponse } from '../../generated/api/employeeProductListResponse.js';
import type { EmployeeProductSelectionResponse } from '../../generated/api/employeeProductSelectionResponse.js';

export interface CopyDeviceListDialogCoCoProps {
  deviceList: EmployeeProductListResponse;
  deviceSelection?: EmployeeProductSelectionResponse;
  onClose: () => void;
}

export const CopyDeviceListDialogCoCo = ({ deviceList, deviceSelection, onClose }: CopyDeviceListDialogCoCoProps) => {
  const navigate = useNavigate();
  const { authenticatedUser } = useAuth();
  // The list is copied to the other companies of the account hierarchy
  const companies = getUserAccounts(authenticatedUser).filter(
    ({ accountMasterId }) => accountMasterId && accountMasterId !== deviceList.accountMdmId
  );
  const [selectedMdmIds, setSelectedMdmIds] = useState<string[]>([]);
  const [error, setError] = useState<string>();
  const [copying, setCopying] = useState(false);
  const copyingRef = useRef(false);
  // The device selections already copied to a company are reused on retry, so that they are not copied twice
  const copiedSelectionIdsRef = useRef<Record<string, string>>({});

  const toggleCompany = (mdmId: string, checked: boolean) => {
    setSelectedMdmIds(ids => (checked ? [...ids, mdmId] : ids.filter(id => id !== mdmId)));
    setError(undefined);
  };

  const closeDialog = () => {
    if (!copyingRef.current) {
      onClose();
    }
  };

  const copySelection = async (mdmId: string) => {
    if (!deviceSelection) {
      return undefined;
    }
    if (!copiedSelectionIdsRef.current[mdmId]) {
      const response = await createEmployeeProductSelection(
        toCopyEmployeeProductSelectionRequest(deviceSelection, mdmId)
      );
      const { id }: EmployeeProductSelectionResponse = await response.json();
      copiedSelectionIdsRef.current[mdmId] = id;
    }
    return copiedSelectionIdsRef.current[mdmId];
  };

  const copyToCompany = async (mdmId: string) =>
    createEmployeeProductList(
      toCopyEmployeeProductListRequest(deviceList, mdmId, await copySelection(mdmId)),
      DeviceListActionCoCo.SAVE_DRAFT
    );

  const copyDeviceList = async () => {
    if (copyingRef.current) {
      return;
    }
    if (selectedMdmIds.length === 0) {
      setError(t.WPOQ('Please select a company to proceed'));
      return;
    }
    copyingRef.current = true;
    setCopying(true);
    setError(undefined);
    const results = await Promise.allSettled(selectedMdmIds.map(copyToCompany));
    copyingRef.current = false;
    setCopying(false);
    const failedMdmIds = selectedMdmIds.filter((_, index) => results[index].status === 'rejected');
    if (failedMdmIds.length === 0) {
      addNotification(
        t.LG3K(
          `Device lists copied as drafts. Select the billing accounts of each list on the companies' own device list page and then publish the lists.`
        )
      );
      onClose();
      navigate(selectedMdmIds.length === 1 ? getDeviceListsTabPath(selectedMdmIds[0]) : paths.COMPANY_INFO_CATALOGS);
      return;
    }
    if (failedMdmIds.length === selectedMdmIds.length) {
      setError(t.JDTO(somethingWentWrongMsg));
      return;
    }
    // Only the failed companies stay selected, so that retrying doesn't create duplicate copies
    setSelectedMdmIds(failedMdmIds);
    setError(t.UZ2G('Selected catalog cannot be copied to all of the selected companies. Please try again.'));
  };

  return (
    <DialogWrapper
      buttons={[
        { color: 'link', text: t.B2V1(cancelMsg), onClick: closeDialog, disabled: copying },
        {
          text: t.UEOZ('Copy'),
          onClick: copyDeviceList,
          isLoading: copying,
          disabled: copying || companies.length === 0,
        },
      ]}
      closeable={!copying}
      header={t.H2ZI('Create a copy of the device list')}
      onCloseDialog={closeDialog}
    >
      {companies.length === 0 ? (
        <p>{t.RB6Z('There are no other companies in the account hierarchy to copy the device list to.')}</p>
      ) : (
        <>
          <p>
            {t.AMXZ(
              'Select the companies to which you want to copy the device list. Be sure to finalize and publish the device list separately.'
            )}
          </p>
          <div>
            {companies.map(({ accountMasterId, name }) => (
              <CL.Checkbox
                key={`${accountMasterId}_${selectedMdmIds.includes(accountMasterId!)}`}
                id={`copy-device-list-company-${accountMasterId}`}
                checked={selectedMdmIds.includes(accountMasterId!)}
                disabled={copying}
                onChange={e => toggleCompany(accountMasterId!, e.currentTarget.checked)}
              >
                {name}
              </CL.Checkbox>
            ))}
          </div>
          {error && <CL.InputError>{error}</CL.InputError>}
          <p className={dsClass.MARGIN_TOP_3}>
            {t.UQMA(
              `You can find the companies' own device lists on their own device list page. Copying does not overwrite existing lists, so delete old lists if necessary.`
            )}
          </p>
        </>
      )}
    </DialogWrapper>
  );
};
